import gql from 'graphql-tag';
import { get, omit, omitBy } from 'lodash';
import {
  useMutation as defaultUseMutation,
  useQuery as defaultUseQuery,
} from '@apollo/react-hooks';

import {
  ApolloInjection,
  BankPaymentSource,
  CreditCardPaymentSource,
  PaymentSource,
  PayPalPaymentSource,
  SupportedPaymentMethod,
  paymentMethodsQuery as PaymentMethodsQuery,
  paymentMethodsQuery_account_paymentSources,
  PaymentSourceType,
  PaymentSourceInput,
  QueryResult,
} from '@zapier/payment/src/types';

type Input = {
  accountId: number | undefined;
  method: PaymentSourceType | null;
  onError: (error: Error, method: PaymentSourceType | null) => void;
  onSuccess: (a: { saveBankPaymentSource: PaymentSource }) => void;
} & ApolloInjection;

type Output = {
  error: ReturnType<typeof defaultUseQuery>['error'] | string | undefined;
  isLoading: boolean;
  isSaving: boolean;
  onSave: (b: PaymentSourceInput, method: PaymentSourceType | null) => void;
  paymentSources: Array<PaymentSource>;
  primarySource: PaymentSource | undefined;
  supportedPaymentMethods: Array<SupportedPaymentMethod>;
};

const paymentSourceFragment = gql`
  fragment paymentSource on PaymentSource {
    details {
      ... on PaymentSourceBank {
        accountHolderName
        accountType
        addressCity
        addressCountry
        addressState
        addressZip
        bankName
        last4
        ownershipType
        routingNumber
        verified
      }

      ... on PaymentSourceCard {
        addressCity
        addressCountry
        addressState
        addressZip
        brand
        expiry
        last4
        name
      }

      ... on PaymentSourcePaypal {
        email
      }
    }
    id
    method
    submethod
  }
`;
const paymentMethodsQuery = gql`
  ${paymentSourceFragment}

  query paymentMethodsQuery($id: Int!) {
    account(id: $id) {
      id
      supportedPaymentMethodsv2 {
        paymentProcessor
        method
      }
      paymentSources {
        id
        ...paymentSource
      }
    }
  }
`;
const saveMutation = {
  [PaymentSourceType.bank]: gql`
    ${paymentSourceFragment}

    mutation SaveBankPaymentSource($accountId: Int!, $data: BankInput!) {
      saveBankPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  [PaymentSourceType.card]: gql`
    ${paymentSourceFragment}

    mutation SaveCardPaymentSource($accountId: Int!, $data: CardInput!) {
      saveCardPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
  [PaymentSourceType.paypal]: gql`
    ${paymentSourceFragment}

    mutation SavePaypalPaymentSource($accountId: Int!, $data: PaypalInput!) {
      savePaypalPaymentSource(accountId: $accountId, data: $data) {
        ...paymentSource
      }
    }
  `,
};

const transformSaveDetails = (
  data: PaymentSourceInput,
  method: PaymentSourceType | null
) => {
  if (method === PaymentSourceType.card) {
    return {
      ...data,
      // Removes all null properties from data these are returned
      // by the tokenization process and can be ignored, otherwise
      // graphql complains / also ignores metadata
      details: omit(
        omitBy(
          get(data, 'details', {}),
          val => val === null || val === undefined || val === ''
        ),
        ['metadata']
      ),
    };
  }

  return data;
};

const usePaymentSources = ({
  accountId,
  method,
  onError,
  onSuccess,
  ...props
}: Input): Output => {
  const useQuery = props.useQuery || defaultUseQuery;
  const useMutation = props.useMutation || defaultUseMutation;
  const queryResults: QueryResult<PaymentMethodsQuery> = useQuery(
    paymentMethodsQuery,
    {
      skip: !accountId,
      variables: {
        id: accountId,
      },
    }
  );
  const [save, saveResults] = useMutation(
    method && method !== PaymentSourceType.manual
      ? saveMutation[method]
      : saveMutation.card,
    {
      awaitRefetchQueries: true,
      // $FlowFixMe - method cannot be null in this situation
      onError: error => onError(error, method),
      onCompleted: onSuccess,
      refetchQueries: () => [
        {
          query: paymentMethodsQuery,
          variables: {
            id: accountId,
          },
        },
      ],
    }
  );

  function isCreditCardSource(
    source: paymentMethodsQuery_account_paymentSources
  ): source is CreditCardPaymentSource {
    if (!source.details) return false;
    return 'brand' in source.details;
  }

  function isBankSource(
    source: paymentMethodsQuery_account_paymentSources
  ): source is BankPaymentSource {
    if (!source.details) return false;
    return 'accountType' in source.details;
  }

  function isPayPalSource(
    source: paymentMethodsQuery_account_paymentSources
  ): source is PayPalPaymentSource {
    if (!source.details) return false;
    return 'email' in source.details;
  }

  const paymentSources: PaymentSource[] = (
    queryResults?.data?.account?.paymentSources ?? []
  ).map(paymentSource => {
    if (isCreditCardSource(paymentSource)) {
      return { ...paymentSource, kind: PaymentSourceType.card };
    } else if (isBankSource(paymentSource)) {
      return { ...paymentSource, kind: PaymentSourceType.bank };
    } else if (isPayPalSource(paymentSource)) {
      return { ...paymentSource, kind: PaymentSourceType.paypal };
    } else {
      return paymentSource as never;
    }
  });

  const onSave = (
    data: PaymentSourceInput,
    selectedMethod: PaymentSourceType | null
  ) => {
    save({
      variables: {
        accountId,
        data: transformSaveDetails(data, selectedMethod),
      },
    });
  };

  const error = queryResults.error
    ? 'There was an issue loading your payment sources. Please try again'
    : saveResults.error;
  return {
    error,
    isLoading: queryResults.loading,
    isSaving: Boolean(saveResults.loading),
    onSave,
    paymentSources,
    primarySource: paymentSources[0],
    // @ts-ignore - SupportedPaymentMethods type doesn't align with the queryResults.
    supportedPaymentMethods: get(
      queryResults,
      ['data', 'account', 'supportedPaymentMethodsv2'],
      []
    ),
  };
};

export default usePaymentSources;
